import React, { useContext } from 'react'
import { RadioButtonGroup } from '@strata-monorepo/shared-ui/components/RadioButtonGroup/RadioButtonGroup'
import { Toggle } from '@strata-monorepo/shared-ui/components/Toggle/Toggle'
import type { FormStepProps } from '@redventures/odyssey'
import { OdysseyContext } from '@redventures/odyssey'
import useFieldSelected from '@strata-monorepo/tracking/src/hooks/useFieldSelected'
import { useStoreState } from '../../store/useStore'

interface RadioGroupOption {
  label: string
  value: string
}

interface RadioGroupComponentProps {
  type: 'radio' | 'segmented'
  variant?: 'outlined' | 'filled'
  title: string
  formValue: string
  options: RadioGroupOption[]
  gridClassName?: string
  tracking?: {
    stepName: string
    stepId: string
    stepNumber: number
    fieldName: string
  }
}

const RadioGroup: React.FC<FormStepProps> = ({ formStep }) => {
  const odysseyContext = useContext(OdysseyContext)
  const trackFieldSelected = useFieldSelected()
  const formId = useStoreState((state) => state.form.formId)
  const formVersion = useStoreState((state) => state.form.formVersion)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const formType = useStoreState((state) => state.form.formType)

  const { componentProps } = formStep
  const {
    title,
    formValue,
    options = [],
    type,
    variant,
    gridClassName,
    tracking,
  } = componentProps as RadioGroupComponentProps

  if (options.length === 0) {
    return null
  }

  const handleChange = (value: string): void => {
    odysseyContext?.setValue(formValue, value)
    triggerFieldSelectedEvent(value)
  }

  const triggerFieldSelectedEvent = (value: string) => {
    // Only track if tracking configuration is provided
    if (!tracking) {
      return
    }

    const selectedOption = options.find((opt) => opt.value === value)

    trackFieldSelected({
      formContext: {
        formName: formVersion,
        formType,
        formId,
      },
      stepContext: {
        stepName: tracking.stepName,
        stepId: tracking.stepId,
        stepNumber: currentStepIndex,
      },
      userInputField: {
        fieldName: tracking.fieldName,
        fieldType: 'button',
        fieldValue: selectedOption?.label || '',
        fieldSource: 'manual',
        autofilled: false,
      },
    })
  }

  return (
    <div>
      {type === 'radio' && (
        <RadioButtonGroup
          options={options}
          value={odysseyContext?.getValue(formValue) as string}
          onChange={handleChange}
          gridClassName={gridClassName}
        />
      )}
      {type === 'segmented' && (
        <Toggle
          variant={variant || 'outlined'}
          label={title}
          options={options}
          values={[odysseyContext?.getValue(formValue) as string]}
          onChange={(values: string[]) => {
            odysseyContext?.setValue(formValue, values[0])
          }}
        />
      )}
      <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
        {odysseyContext?.getError(formValue)}
      </p>
    </div>
  )
}

export default RadioGroup
